import { computeUtilization, parseRawAmount } from '../../../../utils/parsing'
import { exchangeRateAccumulator } from '../../accumulator'
import { createMarketUid } from '../../../../utils'
import { lenderShortName } from '../../../../utils/marketName'
import {
  readSumerCTokenMetadata,
  normalizeSumerGroups,
  SUMER_GROUP_COUNT,
} from '../getters/sumer'
import {
  PRICE_DECIMALS,
  RESERVE_MANTISSA_DECIMALS,
  ConvertEntryOpts,
  resolveMarketPrice,
} from './types'
import { DEFAULT_CLOSE_FACTOR, DEFAULT_LIQUIDATION_PENALTY } from './risk'
import { LenderConfigData } from '../../../../types/lenderTypes'
import { cashLiquidity } from './liquidity'

export function convertSumerEntry(
  opts: ConvertEntryOpts & {
    groups: ReturnType<typeof normalizeSumerGroups>
  },
) {
  const {
    entryRaw,
    asset,
    chainId,
    lender,
    prices,
    additionalYields,
    cToken,
    groups,
  } = opts

  const decs = asset.decimals as number
  const currentEntry = readSumerCTokenMetadata(entryRaw, asset)

  const exchangeRateRaw = currentEntry.exchangeRateCurrent ?? 0n
  const exchangeRateCurrent = parseRawAmount(
    exchangeRateRaw.toString(),
    PRICE_DECIMALS,
  )

  const totalSupplyUnderlying =
    Number(parseRawAmount(currentEntry.totalSupply?.toString(), decs)) *
    Number(exchangeRateCurrent)

  const totalDebt = Number(
    parseRawAmount(currentEntry.totalBorrows?.toString(), decs),
  )

  const liquidity =
    cashLiquidity(currentEntry?.totalCash, decs) ??
    totalSupplyUnderlying - totalDebt
  const price = resolveMarketPrice(prices, asset, chainId)

  const poolId = asset.address
  const collateralActive = Boolean(currentEntry.isListed)
  const borrowingEnabled = Boolean(currentEntry.isBorrowAllowed)

  const marketUid = createMarketUid(chainId, lender, cToken)

  // group-based collateral factors
  const groupId = currentEntry.groupId
  const intraRate = Number(
    parseRawAmount(
      currentEntry.intraRate?.toString(),
      RESERVE_MANTISSA_DECIMALS,
    ),
  )
  const interRate = Number(
    parseRawAmount(
      currentEntry.interRate?.toString(),
      RESERVE_MANTISSA_DECIMALS,
    ),
  )

  // Sumer surfaces liquidation incentives per-market as WAD mantissas
  // (e.g. 1.08e18). Convert to a fraction: mantissa/1e18 - 1. The intra-group
  // (own group) entry uses the homo incentive; inter-group entries use the
  // hetero incentive. Fall back to the default when the contract reports 0.
  const incentiveToPenalty = (raw: bigint): number => {
    const mantissa = Number(parseRawAmount(raw.toString(), PRICE_DECIMALS))
    if (!mantissa) return DEFAULT_LIQUIDATION_PENALTY
    return Math.max(mantissa - 1, 0)
  }
  const homoPenalty = incentiveToPenalty(currentEntry.homoLiquidationIncentive)
  const heteroPenalty = incentiveToPenalty(
    currentEntry.heteroLiquidationIncentive,
  )

  // build config map: one entry per group (0..4)
  // own group → intraRate (override), other groups → interRate (default)
  const config: { [modeId: string]: LenderConfigData } = {}
  for (let gId = 0; gId < SUMER_GROUP_COUNT; gId++) {
    const isOwnGroup = gId === groupId
    const cf = isOwnGroup ? intraRate : interRate
    config[gId] = {
      category: gId,
      borrowCollateralFactor: cf,
      collateralFactor: cf,
      borrowFactor: 1,
      liquidationPenalty: isOwnGroup ? homoPenalty : heteroPenalty,
      closeFactor: DEFAULT_CLOSE_FACTOR,
      collateralDisabled: !collateralActive,
      debtDisabled: !borrowingEnabled,
    }
  }

  return {
    marketUid,
    name: lenderShortName(lender) + ' ' + (asset?.symbol ?? ''),
    poolId,
    asset,
    accumulator: exchangeRateAccumulator(exchangeRateRaw),
    depositRate: currentEntry.depositRate,
    variableBorrowRate: currentEntry.variableBorrowRate,
    stableBorrowRate: 0,
    totalLiquidity: liquidity,
    borrowLiquidity: liquidity,
    totalLiquidityUSD: liquidity * price,
    borrowLiquidityUSD: liquidity * price,
    totalDebt,
    totalDebtUSD: totalDebt * price,
    totalDeposits: totalSupplyUnderlying,
    totalDepositsUSD: totalSupplyUnderlying * price,
    totalDebtStable: 0,
    totalDebtStableUSD: 0,
    utilization: computeUtilization(totalSupplyUnderlying, totalDebt),
    intrinsicYield: additionalYields?.intrinsicYields?.[asset.assetGroup] ?? 0,
    config,
    closeFactor: DEFAULT_CLOSE_FACTOR,
    hasStable: false,
    underlying: asset.address,
    isActive: Boolean(currentEntry.isListed),
    isFrozen: false,
    rewards: [],
    collateralActive,
    borrowingEnabled,
    depositsEnabled: true,
    params: {
      metadata: {
        cToken,
        exchangeRate: exchangeRateRaw.toString(),
        cTokenDecimals: Number(currentEntry.cTokenDecimals?.toString()),
        sumer: {
          isCToken: Boolean(currentEntry.isCToken),
          ...(groups[groupId] ?? groups[0]),
        },
      },
    },
  }
}
